import type { DroitsImage } from '@artosera/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type {
  Bilingue,
  ManqueOeuvre,
  ContexteCompletude,
  Devise,
  Dimensions as DimensionsOeuvre,
  RegimeTva,
  RapportImportMiseAJour,
  StatutOeuvre,
  Tirage,
  TypeEvenementOeuvre,
  TypeExemplaire,
  TypeOeuvre,
} from '@artosera/shared';
import {
  api,
  requeteApiAvecStatut,
  requeteMultipartAvecStatut,
  requeteTelechargement,
  type AppelApi,
} from './client.js';
import type { ApercusSignes, Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Œuvres — `/v1/g/:slug/oeuvres` (`docs/dev/api.md` § 13). Les formes suivent
 * `apps/api/src/oeuvres/presentation.ts` et `oeuvres.routes.ts` au champ près :
 * un seul type ici, jamais un par écran.
 */
export interface OeuvrePresentee {
  readonly manques?: readonly ManqueOeuvre[];
  readonly id: string;
  readonly titre: string;
  readonly slug: string;
  readonly slugPublie: string | null;
  readonly artisteId: string;
  readonly artisteNom: string;
  readonly annee: number | null;
  readonly technique: Bilingue | null;
  readonly dimensions: DimensionsOeuvre | null;
  readonly type: TypeOeuvre;
  readonly tirage: Tirage | null;
  readonly numeroInventaire: string;
  readonly prixPublicCentimes: number | null;
  readonly devise: Devise;
  readonly regimeTva: RegimeTva;
  readonly droitDeSuite: boolean;
  readonly statut: StatutOeuvre;
  /** La vente simple, sans facture (D21, cœur) ; `null` sinon. */
  readonly venteSimple?: {
    readonly contactId: string;
    readonly prixCentimes: number;
    readonly devise: Devise;
    readonly venduLe: string;
  } | null;
  readonly localisationId: string | null;
  readonly mediaPrincipalId: string | null;
  readonly visibleSurLeSite: boolean;
  readonly prixVisible: boolean;
  readonly description: Bilingue | null;
  readonly notesInternes: string;
  readonly etiquettes: readonly string[];
  readonly creeLe: string;
  readonly modifieLe: string;
  readonly prixArtisteCentimes?: number | null;
  readonly prixAchatCentimes?: number | null;
  readonly margeCentimes?: number | null;
}

export interface MediaDeLOeuvre {
  readonly droits?: DroitsImage | null;
  readonly id: string;
  readonly cleOriginale: string;
  readonly variantes: readonly { cle: string; largeur: number; hauteur: number; format: string }[];
  readonly type: string;
  readonly legende: Bilingue | null;
  readonly credit: string | null;
  readonly ordre: number;
}

export interface EditionDeLOeuvre {
  readonly id: string;
  readonly numero: string;
  readonly type: TypeExemplaire;
  readonly statut: StatutOeuvre;
  /** Prix propre de l'exemplaire ; `null` : celui de l'œuvre. */
  readonly prixCentimes?: number | null;
}

export interface FicheOeuvre {
  readonly contexteCompletude?: ContexteCompletude;
  readonly oeuvre: OeuvrePresentee;
  readonly medias: readonly MediaDeLOeuvre[];
  readonly localisation: {
    readonly id: string;
    readonly nom: string;
    readonly type: string;
  } | null;
  readonly editions: readonly EditionDeLOeuvre[];
}

export interface CorpsOeuvre {
  titre: string;
  artisteId: string;
  annee?: number;
  technique?: { fr: string; en?: string };
  dimensions?: {
    hauteur: number;
    largeur: number;
    profondeur?: number;
    diametre?: number;
    uniteOrigine?: 'cm' | 'mm' | 'in';
  };
  type?: TypeOeuvre;
  tirage?: {
    taille: number;
    epreuvesArtiste?: number;
    epreuvesEssai?: number;
    exemplairesSuivis?: boolean;
    vendus?: number;
  };
  numeroInventaire?: string;
  motifInventaire?: string;
  prixPublicCentimes?: number;
  prixArtisteCentimes?: number;
  prixAchatCentimes?: number;
  devise?: Devise;
  regimeTva?: RegimeTva;
  droitDeSuite?: boolean;
  localisationId?: string | null;
  mediaPrincipalId?: string | null;
  visibleSurLeSite?: boolean;
  prixVisible?: boolean;
  description?: { fr: string; en?: string };
  notesInternes?: string;
  etiquettes?: readonly string[];
}

/** À la mise à jour, `null` efface un prix (« — »), à ne pas confondre avec 0. */
export type CorpsMiseAJourOeuvre = Partial<
  Omit<CorpsOeuvre, 'prixPublicCentimes' | 'prixArtisteCentimes' | 'prixAchatCentimes'>
> & {
  slug?: string;
  prixPublicCentimes?: number | null;
  prixArtisteCentimes?: number | null;
  prixAchatCentimes?: number | null;
};

export interface FiltresOeuvres {
  incompletes?: 'site';
  vignettes?: boolean;
  q?: string;
  statut?: readonly StatutOeuvre[];
  inclureArchivees?: boolean;
  artisteId?: string;
  localisationId?: string;
  visibleSurSite?: boolean;
  prixVisible?: boolean;
  prixMin?: number;
  prixMax?: number;
  annee?: number;
  type?: TypeOeuvre;
  tri?:
    | 'pertinence'
    | 'recent'
    | 'ancien'
    | 'titre'
    | 'artiste'
    | 'inventaire'
    | 'prix_croissant'
    | 'prix_decroissant';
  page?: number;
  parPage?: number;
}

export interface ReponseListeOeuvres {
  oeuvres: readonly OeuvrePresentee[];
  pagination: Pagination;
  vignettes?: ApercusSignes;
  prixArtisteVisible: boolean;
}

export interface EntreeHistorique {
  readonly id: string;
  readonly action: string;
  readonly resume: string;
  readonly acteurEmail: string;
  readonly date: string;
}

export interface RapportImport {
  readonly essai?: boolean;
  readonly total: number;
  readonly crees: number;
  readonly refuses: number;
  readonly artistesCrees: readonly string[];
  readonly imagesDeposees: number;
  /** Images d'une œuvre créée qui n'ont pas pu être déposées. */
  readonly imagesNonDeposees?: number;
  readonly colonnesInconnues: readonly string[];
  readonly erreurs: readonly LigneRapportImport[];
  /** Doublons probables, images non déposées : la ligne est créée. */
  readonly avertissements?: readonly LigneRapportImport[];
}

export interface LigneRapportImport {
  readonly ligne: number;
  readonly titre: string;
  readonly erreurs: readonly string[];
}

export interface ReponseImport {
  readonly statutHttp?: number;
  readonly importId?: string;
  readonly etat:
    'termine' | 'enfile' | 'en_cours' | 'echoue' | 'abandonne' | 'ignore_module_inactif';
  readonly message?: string;
  readonly finalisation?: 'incomplete';
  readonly travailId?: string;
  readonly lignes?: number;
  readonly rapport: RapportImport | null;
}

export interface LigneRapportOeuvres {
  readonly ligne: number;
  readonly libelle: string;
  readonly messages: readonly string[];
}

/** Le rapport d'import d'œuvres, prêt pour la vue : compteurs, détails et listes. */
export interface RapportOeuvres {
  readonly essai: boolean;
  readonly total: number;
  readonly crees: number;
  readonly refuses: number;
  readonly colonnesInconnues: readonly string[];
  readonly erreurs: readonly LigneRapportOeuvres[];
  readonly avertissements: readonly LigneRapportOeuvres[];
  readonly details: Readonly<Record<string, number>>;
  readonly listes: Readonly<Record<string, readonly string[]>>;
}

/** Le rapport de l'import d'œuvres (6.1) dans la forme que la vue affiche. */
export function rapportDesOeuvres(rapport: RapportImport): RapportOeuvres {
  const ligne = (entree: LigneRapportImport): LigneRapportOeuvres => ({
    ligne: entree.ligne,
    libelle: entree.titre,
    messages: entree.erreurs,
  });
  return {
    essai: rapport.essai ?? false,
    total: rapport.total,
    crees: rapport.crees,
    refuses: rapport.refuses,
    colonnesInconnues: rapport.colonnesInconnues,
    erreurs: rapport.erreurs.map(ligne),
    avertissements: (rapport.avertissements ?? []).map(ligne),
    details: {
      imagesDeposees: rapport.imagesDeposees,
      imagesNonDeposees: rapport.imagesNonDeposees ?? 0,
    },
    listes: { artistesCrees: rapport.artistesCrees },
  };
}

export function requeteFiltres(filtres: FiltresOeuvres): string {
  const params = new URLSearchParams();
  if (filtres.incompletes) params.set('incompletes', filtres.incompletes);
  if (filtres.vignettes) params.set('vignettes', 'true');
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.statut && filtres.statut.length > 0) params.set('statut', filtres.statut.join(','));
  if (filtres.inclureArchivees) params.set('inclureArchivees', 'true');
  if (filtres.artisteId) params.set('artisteId', filtres.artisteId);
  if (filtres.localisationId) params.set('localisationId', filtres.localisationId);
  if (filtres.visibleSurSite !== undefined) {
    params.set('visibleSurSite', String(filtres.visibleSurSite));
  }
  if (filtres.prixVisible !== undefined) params.set('prixVisible', String(filtres.prixVisible));
  if (filtres.prixMin !== undefined) params.set('prixMin', String(filtres.prixMin));
  if (filtres.prixMax !== undefined) params.set('prixMax', String(filtres.prixMax));
  if (filtres.annee !== undefined) params.set('annee', String(filtres.annee));
  if (filtres.type) params.set('type', filtres.type);
  params.set('tri', filtres.tri ?? 'recent');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 50));
  return params.toString();
}

/** Le corps d'une vente simple : acheteur du carnet, prix, date civile. */
export interface CorpsVenteSimple {
  readonly contactId: string;
  readonly prixCentimes: number;
  readonly devise?: Devise;
  /** Date CIVILE `AAAA-MM-JJ` ; aujourd'hui par défaut. */
  readonly venduLe?: string;
}

export const oeuvresApi = {
  lister: (galerie: string, filtres: FiltresOeuvres = {}, appel?: AppelApi) =>
    api.get<ReponseListeOeuvres>(
      `/g/${encodeURIComponent(galerie)}/oeuvres?${requeteFiltres(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<FicheOeuvre>(`/g/${encodeURIComponent(galerie)}/oeuvres/${id}`, { galerie, ...appel }),
  creer: (galerie: string, corps: CorpsOeuvre & { vendeurContactId?: string }, appel?: AppelApi) =>
    api.post<{ oeuvre: OeuvrePresentee }>(`/g/${encodeURIComponent(galerie)}/oeuvres`, corps, {
      galerie,
      ...appel,
    }),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourOeuvre, appel?: AppelApi) =>
    api.patch<{ oeuvre: OeuvrePresentee }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}`,
      corps,
      { galerie, ...appel },
    ),
  changerStatut: (
    galerie: string,
    id: string,
    corps: { evenement: TypeEvenementOeuvre } | { statut: StatutOeuvre; raison: string },
    appel?: AppelApi,
  ) =>
    api.post<{ oeuvre: OeuvrePresentee; evenement: string }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/statut`,
      corps,
      { galerie, ...appel },
    ),
  /** La vente simple, sans facture (D21, cœur) : `POST …/oeuvres/:id/vente`. */
  marquerVendue: (galerie: string, id: string, corps: CorpsVenteSimple, appel?: AppelApi) =>
    api.post<{ oeuvre: OeuvrePresentee }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/vente`,
      corps,
      { galerie, ...appel },
    ),
  archiver: (galerie: string, id: string, raison?: string, appel?: AppelApi) =>
    api.post<{ oeuvre: OeuvrePresentee }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/archiver`,
      raison ? { raison } : {},
      { galerie, ...appel },
    ),
  ordonnerMedias: (galerie: string, id: string, medias: readonly string[], appel?: AppelApi) =>
    api.put<{ medias: readonly { id: string; ordre: number }[] }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/medias/ordre`,
      { medias },
      { galerie, ...appel },
    ),
  historique: (galerie: string, id: string, page = 1, appel?: AppelApi) =>
    api.get<{ historique: readonly EntreeHistorique[]; pagination: Pagination }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/historique?page=${String(page)}`,
      { galerie, ...appel },
    ),
  editions: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{
      type: TypeOeuvre;
      tirage: Tirage | null;
      compteur: { total: number; vendus: number; disponibles: number };
      editions: readonly EditionDeLOeuvre[];
    }>(`/g/${encodeURIComponent(galerie)}/oeuvres/${id}/editions`, { galerie, ...appel }),
  creerEditions: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ crees: number; deja: readonly string[]; editions: readonly EditionDeLOeuvre[] }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${id}/editions`,
      {},
      { galerie, ...appel },
    ),
  numeroPropose: (galerie: string, artisteId?: string, appel?: AppelApi) =>
    api.get<{ numeroInventaire: string }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/numero-inventaire${
        artisteId ? `?artisteId=${artisteId}` : ''
      }`,
      { galerie, ...appel },
    ),
  exporter: (
    galerie: string,
    format: 'csv' | 'xlsx',
    filtres: FiltresOeuvres = {},
    appel?: AppelApi,
  ) => {
    // Les filtres de la liste, sans pagination ni tri : l'export rend ce que
    // la liste montre (recette import/export, défaut 19).
    const params = new URLSearchParams();
    if (filtres.incompletes) params.set('incompletes', filtres.incompletes);
    params.set('format', format);
    if (filtres.q) params.set('q', filtres.q);
    if (filtres.statut && filtres.statut.length > 0) params.set('statut', filtres.statut.join(','));
    if (filtres.artisteId) params.set('artisteId', filtres.artisteId);
    if (filtres.localisationId) params.set('localisationId', filtres.localisationId);
    if (filtres.visibleSurSite !== undefined) {
      params.set('visibleSurSite', String(filtres.visibleSurSite));
    }
    if (filtres.prixVisible !== undefined) params.set('prixVisible', String(filtres.prixVisible));
    if (filtres.prixMin !== undefined) params.set('prixMin', String(filtres.prixMin));
    if (filtres.prixMax !== undefined) params.set('prixMax', String(filtres.prixMax));
    if (filtres.annee !== undefined) params.set('annee', String(filtres.annee));
    if (filtres.type) params.set('type', filtres.type);
    if (filtres.inclureArchivees) params.set('inclureArchivees', 'true');
    return requeteTelechargement(
      `/g/${encodeURIComponent(galerie)}/oeuvres/export?${params.toString()}`,
      { galerie, ...appel },
    );
  },
  importer: async (
    galerie: string,
    fichier: File,
    images: File | null,
    options: { readonly motifInventaire?: string; readonly essai?: boolean } = {},
    appel?: AppelApi,
  ) => {
    const donnees = new FormData();
    donnees.set('fichier', fichier);
    if (images) donnees.set('images', images);
    if (options.motifInventaire) donnees.set('motifInventaire', options.motifInventaire);
    donnees.set('essai', options.essai === true ? 'true' : 'false');
    const reponse = await requeteMultipartAvecStatut<ReponseImport>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/import`,
      donnees,
      { galerie, ...appel },
    );
    return { ...reponse.donnees, statutHttp: reponse.statut };
  },
  importerMiseAJour: async (
    galerie: string,
    fichier: File,
    demande: DemandeImportMiseAJour,
    appel: AppelApi,
  ) => {
    const donnees = new FormData();
    donnees.set('fichier', fichier);
    donnees.set('essai', demande.essai ? 'true' : 'false');
    if (!demande.essai) donnees.set('empreinteApercu', demande.empreinteApercu);
    const reponse = await requeteMultipartAvecStatut<RapportImportMiseAJour>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/import/mise-a-jour`,
      donnees,
      { galerie, ...appel, ...(!demande.essai ? { cleOperation: demande.cleOperation } : {}) },
    );
    return reponse.donnees;
  },
  gabarit: (galerie: string, appel?: AppelApi) =>
    requeteTelechargement(`/g/${encodeURIComponent(galerie)}/oeuvres/import/gabarit.csv`, {
      galerie,
      ...appel,
    }),
  suivreImport: async (galerie: string, importId: string, appel?: AppelApi) => {
    const reponse = await requeteApiAvecStatut<ReponseImport & { importId: string }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/import/${encodeURIComponent(importId)}`,
      { galerie, ...appel },
    );
    return { ...reponse.donnees, statutHttp: reponse.statut };
  },
};

const cleListe = (galerie: string, filtres: FiltresOeuvres) =>
  cleMetier(galerie, 'oeuvres', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'oeuvres', 'fiche', id);
const cleHistorique = (galerie: string, id: string) =>
  cleMetier(galerie, 'oeuvres', 'historique', id);
const cleEditions = (galerie: string, id: string) => cleMetier(galerie, 'oeuvres', 'editions', id);

export function useOeuvres(
  galerie: string,
  filtres: FiltresOeuvres = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => oeuvresApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
    enabled: options.enabled ?? true,
  });
}

export function useOeuvre(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => oeuvresApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

export function useHistoriqueOeuvre(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleHistorique(galerie, id ?? ''),
    queryFn: (contexte) =>
      oeuvresApi.historique(galerie, id ?? '', undefined, appelDeRequete(contexte)),
    select: (reponse) => reponse.historique,
    enabled: id !== null,
  });
}

export function useEditionsOeuvre(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleEditions(galerie, id ?? ''),
    queryFn: (contexte) => oeuvresApi.editions(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

export function useNumeroInventaireExpected(galerie: string, artisteId?: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'oeuvres', 'numero-propose', artisteId ?? null),
    queryFn: (contexte) => oeuvresApi.numeroPropose(galerie, artisteId, appelDeRequete(contexte)),
    select: (reponse) => reponse.numeroInventaire,
    staleTime: 0,
  });
}

function invaliderOeuvres(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres', 'liste') });
}

export function useCreerOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsOeuvre & { vendeurContactId?: string }, appel) =>
        oeuvresApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderOeuvres(client, galerie),
    }),
  );
}

export function useModifierOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourOeuvre }, appel) =>
        oeuvresApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderOeuvres(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

/**
 * Changement de statut, avec optimisme (mission S3-a) : la pastille change
 * avant la réponse du serveur, et revient en arrière si l'API refuse la
 * transition (`409 oeuvre.transition_interdite`) ou exige une raison.
 */
export function useChangerStatutOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        {
          id,
          corps,
        }: {
          id: string;
          corps: { evenement: TypeEvenementOeuvre } | { statut: StatutOeuvre; raison: string };
          statutOptimiste: StatutOeuvre;
        },
        appel,
      ) => oeuvresApi.changerStatut(galerie, id, corps, appel),
      onMutate: async ({ id, statutOptimiste }) => {
        const cle = cleFiche(galerie, id);
        await client.cancelQueries({ queryKey: cle });
        const precedent = client.getQueryData<FicheOeuvre>(cle);
        if (precedent) {
          client.setQueryData(cle, {
            ...precedent,
            oeuvre: { ...precedent.oeuvre, statut: statutOptimiste },
          });
        }
        return { precedent };
      },
      onError: (_erreur, variables, contexte) => {
        if (contexte?.precedent)
          client.setQueryData(cleFiche(galerie, variables.id), contexte.precedent);
      },
      onSettled: (_donnees, _erreur, variables) => {
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
        invaliderOeuvres(client, galerie);
      },
    }),
  );
}

/** « Marquer vendue » : la vente simple du cœur, sans facture (D21). */
export function useMarquerVendue(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsVenteSimple }, appel) =>
        oeuvresApi.marquerVendue(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderOeuvres(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useArchiverOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, raison }: { id: string; raison?: string }, appel) =>
        oeuvresApi.archiver(galerie, id, raison, appel),
      onSuccess: (_donnees, variables) => {
        invaliderOeuvres(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

/** Ordre des médias, avec optimisme (mission S3-a) : le glisser-déposer répond aussitôt. */
export function useOrdonnerMediasOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, medias }: { id: string; medias: readonly string[] }, appel) =>
        oeuvresApi.ordonnerMedias(galerie, id, medias, appel),
      onMutate: async ({ id, medias }) => {
        const cle = cleFiche(galerie, id);
        await client.cancelQueries({ queryKey: cle });
        const precedent = client.getQueryData<FicheOeuvre>(cle);
        if (precedent) {
          const parId = new Map(precedent.medias.map((media) => [media.id, media]));
          const reordonnes = medias
            .map((mediaId, ordre) => {
              const media = parId.get(mediaId);
              return media ? { ...media, ordre } : null;
            })
            .filter((media): media is MediaDeLOeuvre => media !== null);
          client.setQueryData(cle, {
            ...precedent,
            medias: reordonnes,
            oeuvre: { ...precedent.oeuvre, mediaPrincipalId: medias[0] ?? null },
          });
        }
        return { precedent };
      },
      onError: (_erreur, variables, contexte) => {
        if (contexte?.precedent)
          client.setQueryData(cleFiche(galerie, variables.id), contexte.precedent);
      },
      onSettled: (_donnees, _erreur, variables) => {
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useCreerEditionsOeuvre(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => oeuvresApi.creerEditions(galerie, id, appel),
      onSuccess: (_donnees, id) => {
        void client.invalidateQueries({ queryKey: cleEditions(galerie, id) });
        void client.invalidateQueries({ queryKey: cleFiche(galerie, id) });
      },
    }),
  );
}

export function useImporterOeuvres(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        {
          fichier,
          images,
          motifInventaire,
        }: {
          fichier: File;
          images: File | null;
          motifInventaire?: string;
        },
        appel,
      ) =>
        oeuvresApi.importer(
          galerie,
          fichier,
          images,
          motifInventaire === undefined ? {} : { motifInventaire },
          appel,
        ),
      onSuccess: () => invaliderOeuvres(client, galerie),
    }),
  );
}

export const cleListeOeuvres = cleListe;
export const cleFicheOeuvre = cleFiche;

export type DemandeImportMiseAJour =
  | { readonly essai: true }
  | { readonly essai: false; readonly empreinteApercu: string; readonly cleOperation: string };

export function useImportMiseAJour(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        { fichier, demande }: { fichier: File; demande: DemandeImportMiseAJour },
        appel,
      ) => oeuvresApi.importerMiseAJour(galerie, fichier, demande, appel),
      onSuccess: (rapport) => {
        if (!rapport.essai)
          void client.invalidateQueries({ queryKey: cleMetier(galerie, 'oeuvres') });
      },
    }),
  );
}
